/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import {
  IDexfSettings,
  IProductContent,
  IProductReason,
  IShoppingItem,
  IStoreAvailability,
  IZipAvailability
} from '@inter-ikea-kompis/types/lib';
import { AOQualityPreset, IVector2, IVector3 } from '@inter-ikea-betrakta/viewer';
import { DexfSettingsFilterSegmentEnum } from '@inter-ikea-kompis/enums';
import { FloorMaterial } from './config/defaultRenderSettings';
import { Vector, Entity } from '@inter-ikea-core/core';

// The images returned from the products api has the following sizes
// S1 40x40
// S2 110x110
// S3 250x250
// S4 500x500
// S5 2000x2000
export type ImageSize = 'S1' | 'S2' | 'S3' | 'S4' | 'S5';

export enum DEXFStockProbability {
  high = 'HIGH_IN_STOCK',
  medium = 'MEDIUM_IN_STOCK',
  low = 'LOW_IN_STOCK',
  out = 'OUT_OF_STOCK'
}

export type Coord2d = {
  X: number;
  Y: number;
};

export type Coord3d = Coord2d & {
  Z: number;
};

export interface ProductEntity {
  entityId: number;
  productId: string;
}

export interface ProductEntityWithPosition extends ProductEntity {
  worldPosition: Coord3d | null;
  canvasPosition: Coord2d | null;
}

export interface EntityWithPosition {
  entityId: number;
  x: number;
  y: number;
}

export interface SerializableEntity {
  id: string;
  ref: string;
  parent: string;
  needsResnap: boolean;
  c: any;
}

export interface Conflict {
  entityId: number;
  solve: {
    action: string;
    params: any;
  }[];
  messages: {
    title: string;
    info: string;
    yesButton: string;
    noButton: string;
  };
}

/**
 * Same as AxisAlignedBox but with lowercase x, y, z in order to send it to the viewer
 */
export type OuterMeasurements = {
  width: number;
  height: number;
  depth: number;
  min: {
    x: number;
    y: number;
    z: number;
  };
  max: {
    x: number;
    y: number;
    z: number;
  };
};

export type LineCenterPoint3D = {
  position: IVector3;
  dim: Dimension;
  wall: string;
};

export type LineCenterPoint2D = {
  x: number;
  y: number;
  dim: Dimension;
  wall: string;
};

export type InnerLineCenterPoint2D = LineCenterPoint2D & {
  distance: number;
};

export type InnerLineCenterPoint3D = LineCenterPoint3D & {
  distance: number;
};

export type LabelPosition = {
  dim: string;
  size: string;
  x: number;
  y: number;
  visible?: boolean;
  rotation?: string;
  bgColor?: string;
  color?: string;
  bigText?: boolean;
  distance?: number;
};

export type RoomOptions = {
  wallColor?: string;
  dimensions?: Dimensions;
  floorMaterial?: FloorMaterial;
};

export type RenderSettings = Readonly<{
  spotLight: {
    intensity: number;
    color: { r: number; b: number; g: number };
    positionAdjustment: { y: number; z: number };
  };
  lineLight: { intensity: number; color: { r: number; b: number; g: number }; angleRange: number };
  roomMaterials: { id: string; url: string; previewUrl: string; colorCode?: string }[];
  environmentMap: {
    intensity: number;
    rotation: number;
    localMode: {
      size: { x: number; y: number; z: number };
      position: { x: number; y: number; z: number };
    };
    assemblyInstructionIntensity: number;
    url: string;
  };
  mainLight: {
    position: IVector3;
    intensity: number;
    outerAngleDegrees: number;
    innerAngleDegrees: number;
    shadow: {
      shadowNear: number;
      shadowFar: number;
      shadowResolution: number;
      enableShadows: boolean;
      bias: { z_units: number; z_factor: number };
    };
    colorTemperature: number;
  };
  floorLight: { intensity: number; color: { r: number; b: number; g: number } };
  directionalLight: {
    enabled: boolean;
    intensity: number;
    color: { r: number; b: number; g: number };
    enableShadows: boolean;
    shadowFar: number;
    shadowNear: number;
    shadowResolution: number;
    position: { x: number; y: number; z: number };
    target: { x: number; y: number; z: number };
  };
  pointLight: {
    enabled: boolean;
    intensity: number;
    enableShadows: boolean;
    position: { x: number; y: number; z: number };
    color: { r: number; b: number; g: number };
    shadowNear: number;
    shadowFar: number;
    shadowResolution: number;
  };
  ceilingLight: { intensity: number; color: { r: number; b: number; g: number } };
  temporalEffects: {
    temporalAntiAliasing: boolean;
    screenSpaceAmbientOcclusion: boolean;
    ssaoParams: {
      enabled: boolean;
      radius: number;
      ambientOccluionStrength: number;
    };
  };
  darkMode: {
    environmentMapIntensity: number;
    glow: {
      enabled: boolean;
      blurSize: number;
      intensity: number;
    };
    directionalLight: {
      intensity: number;
    };
    lineLight: {
      intensity: number;
      angle: number;
    };
    spotLight: {
      intensity: number;
      angle: number;
      width: number;
    };
    temporalEffects: {
      temporalAntiAliasing: boolean;
      screenSpaceAmbientOcclusion: boolean;
      ssaoParams: {
        enabled: boolean;
        radius: number;
        ambientOccluionStrength: number;
      };
      quality: AOQualityPreset;
    };
  };
}>;

export interface Part {
  itemType: string;
  id: string;
  localId: string;
  product?: ProductPartial;
  complimentary: boolean;
}

export interface Appearance {
  id: string;
  color: string;
  name: string;
}

interface PriceInformation {
  hasFamilyPrice: boolean;
  currentPrice: number;
  regularPrice: number;
  nbrOfItemsInPackage: number;
  nbrOfItemsInPackageWithText?: string;
  packageText?: string;
}

/**
 * Always in millimeter
 */
export interface Dimensions {
  width: number;
  height: number;
  depth: number;
}

export type Dimension = keyof Dimensions;

export type TextMeasures = {
  width: string;
  height: string;
  depth: string;
  unit: string;
};

export type DimensionsAttr = keyof Dimensions;

export enum ProductGoodToKnowTypeName {
  WARNING = 'Warning',
  COMPL_ASSEMBLY_INFORMATION = 'Compl. assembly information',
  SOLD_SEPARATELY = 'Sold separately'
}

export type ProductGoodToKnow = {
  typeName: ProductGoodToKnowTypeName;
  text: string;
};

export enum ImageType {
  MPP = 'Main Product Picture',
  FPP = 'Functional Product Picture',
  QPP = 'Quality Product Picture',
  IPP = 'Inspirational Product Picture',
  MIP = 'Measurement Illustration',
  CPP = 'Context Product Picture',
  NSPP = 'Non-Standardized Product Picture'
}

export interface ImageSource {
  src: string;
  width: number;
  height: number;
}

export interface ProductImageDetails {
  id: string;
  type: ImageType;
  sources: ImageSource[];
}

export interface StoreAvailability {
  stockAvailability: number;
  inStockProbability: DEXFStockProbability;
}

export interface HomeDeliveryAvailability {
  availableForHomeDelivery: boolean;
  messageType: string | undefined;
  quantity: number | undefined;
}

type ApplicableFrameType = 'hinged' | 'non-hinged' | 'cornerRegularFrame';

export type ProductMetaData = {
  design?: string;
  allowedHandleTypes?: HandleType[];
  allowOnCornerAddonFrame?: boolean;
  allowOnCornerRegularFrame?: boolean;
  handleType?: HandleType;
  image?: string;
  defaultFrontLayout?: string;
  matchesColors?: string[];
  applicableFrameTypes?: ApplicableFrameType[];
  replacedBy?: string;
  ageGroup?: string;
  nbrOfHandles?: number;
  lightSourceWidth?: number;
  lightSourceType?: string;
  needsSoftClosingDevice?: boolean;
  lightAngle?: number;
  lightIntensity?: number;
  productImage?: { type: keyof typeof ImageType; index: number };
  loftBedOrientation?: { mirrored: boolean; 'inward-desk': boolean; 'inward-cabinet': boolean };
  addon?: boolean;
  color?: string[];
  relatedArticle?: string;
};

export interface RangeTemplateData {
  id: string;
  size: { width: number; height: number; depth: number };
  comSize: { width: number; height: number; depth: number };
  family?: string;
  parts?: { id: string; partKey: string; main?: boolean; include?: boolean }[];
  modelTransform?: any;
  dexfAlias?: string;
}

export interface RangeProduct {
  id: string;
  type?: string;
  dexf?: string;
  desc?: string;
  range?: string;
  template: RangeTemplateData;
  typeCode?: number;
  partsInPackage?: number;
  autoAdaptGroup?: { key: string; priority: number } | string | null;
  meta?: any;
}

export interface RangeCatalog {
  range: string;
  schemaVersion: string;
  rangeVersion: string;
  products: RangeProduct[];
}

export interface ProductTemplate {
  templateId: string;
  name: string;
  inherits?: string[];
  components: any;
}

export interface RangeTemplate {
  range: string;
  schemaVersion: string;
  rangeVersion: string;
  assetSource: string;
  templates: ProductTemplate[];
}

export interface Product {
  valid: boolean;
  /** Product id as used in Core */
  coreProductId: string;
  entityId: number;
  name: string;
  images: ProductImageDetails[];
  type: ProductType;
  typeName: string;
  goodToKnow: ProductGoodToKnow[];
  dimensions: Dimensions;
  communicatedDimensions: Dimensions;
  communicatedDimensionsLocalized: TextMeasures;
  /** Dexf measureReference.textMetric */
  communicatedSizeText: string;
  /** Dexf validDesignText */
  designText: string;
  design: ProductDesign;
  priceInformation: PriceInformation;
  /** Appearance of product, material or color */
  appearances: Appearance[];
  globalItemNo: string;
  templateId: string;
  meta?: ProductMetaData;
  /** parts that should be added to the shopping list when buying the product */
  parts: Part[];
  /** article number for the current market (local article number) */
  ruItemNo: string;
  wattage?: number;
  reason: IProductReason[];
  storeAvailability?: IStoreAvailability;
  zipAvailability?: IZipAvailability;
  length?: number;
  complainceInformation?: any;
  content?: IProductContent | null;
}

export type ProductPartial = Pick<
  Product,
  | 'coreProductId'
  | 'ruItemNo'
  | 'name'
  | 'goodToKnow'
  | 'type'
  | 'communicatedDimensions'
  | 'dimensions'
  | 'images'
  | 'typeName'
  | 'designText'
  | 'communicatedSizeText'
  | 'priceInformation'
  | 'valid'
  | 'storeAvailability'
  | 'meta'
>;

export type NonMarketProduct = Pick<
  Product,
  | 'coreProductId'
  | 'globalItemNo'
  | 'type'
  | 'dimensions'
  | 'communicatedDimensions'
  | 'design'
  | 'meta'
>;

export interface VPCConfiguration {
  catalogs: any;
  entities: any[];
  version: string;
  kvadratVpc: string | undefined;
  wallShape?: WallShape;
}

export interface SaveVPCResponse {
  application: string;
  configurationId: string;
  configurationVersion: string;
  configuration: VPCConfiguration;
}

export enum VPCTypeCode {
  MAIN_DESIGN_IMAGE = 'main-design-image',
  OPEN_DESIGN_IMAGE = 'open-design-image',
  ANGLED_DESIGN_IMAGE = 'open-design-image-angled'
}

export interface VPCImageURL {
  typeCode: string;
  url: string;
}

export interface LoadVPCResponse {
  application: string;
  configuration: VPCConfiguration;
  configurationId: string;
  configurationVersion: string;
  image?: string;
  imageUrl?: [VPCImageURL];
}

export type ShoppingItemWithPrice = IShoppingItem & { price: number };

export type KompisSettings = {
  consumer: string;
  contract: string;
  locale: string;
  applicationName: string;
  applicationNameTranslated: string;
  translationEndpointApplication: string;
  translationPrefixApplication: string;
  dexfApplicationId: string; // storageonepax, storageonebesta
  uiPlatform: string;
  environment:
    | 'production'
    | 'test'
    | 'development'
    | 'verification'
    | 'production-next-latest'
    | 'verification-pull-request';
  dexfApiKey: string;
  localizationPrefixApplication: string;
  localizationEndpoints: string[];
  translationEndpoints: string[];
  redirectUri?: string;
  filterSegment: DexfSettingsFilterSegmentEnum;
  dexfConfigurationVersion?: string;
  settings: IDexfSettings;
};

export enum ShapeTypeEnum {
  sloped = 'slopedRight',
  slopedLeft = 'slopedLeft',
  squared = 'squared',
  off = 'off',
  squaredCornerRight = 'squaredCornerRight'
}

export type WallShape = {
  type: ShapeTypeEnum;
  dimensions: Vector[];
};

export enum ProductType {
  HANDLE = 'handle',
  KNOB = 'knob',
  EDGE_HANDLE = 'edge-handle',
  HANDLE_DRILL_TEMPLATE = 'handle-drill-template',
  CHRISTMAS_PROPPING = 'christmas-propping',
  PEGBOARD = 'pegboard',
  LIGHT_DRIVER = 'light-driver',
  LIGHT_CORD = 'light-cord',
  LIGHT_CONTROLER = 'light-controler',
  CLOTH_HANGERS = 'cloth-hangers',
  TV = 'tv',
  TV_WITH_FOOT = 'tv-with-foot',
  // PAX
  PAX_FRAME = 'pax-hinge-frame',
  PAX_HINGE_DOOR_ASM = 'pax-door-assembly',
  PAX_HINGE_DOOR = 'pax-hinge-door-softclosing',
  PAX_SOFT_CLOSING_HINGE = 'pax-soft-closing-hinge',
  PAX_SLIDING_DOOR_REGULAR_ASM = 'pax-sliding-door-assembly',
  PAX_SLIDING_DOOR_REGULAR_FRAME = 'pax-sliding-door-frame',
  PAX_SLIDING_DOOR_WITH_PANELS_ASM = 'pax-sliding-door-with-panels-assembly',
  PAX_SLIDING_DOOR_PANEL_FRAME = 'pax-sliding-door-with-panels-frame',
  PAX_SLIDING_DOOR_PANEL = 'pax-sliding-door-panel',
  PAX_SLIDING_DOOR_REGULAR_FRONT = '26106',
  PAX_SLIDING_DOOR_PANEL_FRONT = '29612',
  PAX_SOFT_CLOSING_DEVICE_SLIDING_DOORS = '29186',
  PAX_SOFT_OPENING_CLOSING_DEVICE_SLIDING_DOORS = '35676',
  PAX_SHELF = 'pax-shelf',
  PAX_MESH_BASKET_ASM = 'pax-mesh-basket-assembly',
  PAX_METAL_BASKET_ASM = 'pax-metal-basket-assembly',
  PAX_MESH_BASKET = 'pax-mesh-basket',
  PAX_METAL_BASKET = 'pax-metal-basket',
  PAX_MESH_BASKET_RAIL = 'pax-pullout-rail-for-baskets',
  PAX_PULL_OUT_TROUSER_HANGER = 'pax-pull-out-trouser-hanger',
  PAX_CLOTH_RAIL_580 = 'pax-cloth-rail-580',
  PAX_CLOTH_RAIL_350 = 'pax-cloth-rail-350',
  PAX_GLASS_SHELF = 'pax-glass-shelf',
  PAX_DRAWER = 'pax-drawer',
  PAX_PULL_OUT_TRAY = 'pax-pull-out-tray',
  PAX_PULL_OUT_TRAY_WITH_DIVIDER_INSERTS_ASM = 'pax-pull-out-tray-with-divider-inserts',
  PAX_PULL_OUT_TRAY_WITH_COMPARTMENT_INSERTS_ASM = 'pax-pull-out-tray-with-comp-inserts',
  PAX_PULL_OUT_TRAY_WITH_SHOE_INSERTS_ASM = 'pax-pull-out-tray-with-shoe-inserts',
  PAX_PULL_OUT_TRAY_WITH_MAT_ASM = 'pax-pull-out-tray-with-mat',
  PAX_PULL_OUT_TRAY_INSERT = 'p_out_tray_insert',
  PAX_DIVIDER_FOR_PULL_OUT_TRAY = 'div-f-p-out-tray',
  PAX_SHOE_INSERT_FOR_PULL_OUT_TRAY = 'shoe-insert-f-p-out-tray',
  PAX_INSERT_WITH_4_COMP = 'insert-w-4-comp',
  PAX_INSERT_FOR_JEWELLERY = 'insert-for-jewellery',
  PAX_DRAWER_MAT = 'pax-drawer-mat',
  PAX_DRAWER_BOX_ASM = 'komp_box_set_assembly',
  PAX_DRAWER_BOX = 'box',
  PAX_GLASS_DRAWER = 'pax-glass-drawer',
  PAX_PULL_OUT_SHOE_SHELF = 'pax-pull-out-shoe-shelf',
  PAX_PULL_OUT_CLOTH_RAIL = 'pax-pull-out-cloth-rail',
  PAX_PULL_OUT_STORAGE_BAG = 'pax-pull-out-storage-bag',
  PAX_DRAWER_FRAMED_GLASS = 'pax-drawer-with-framed-glass-front',
  PAX_DRAWER_FRAMED = 'pax-drawer-with-framed-front',
  PAX_DRAWER_LOCKABLE = 'pax-drawer-lockable',
  PAX_SHOE_SHELF = 'pax-shoe-shelf',
  PAX_VALET_HANGER = 'pax-valet-hanger',
  PAX_T_DIVIDER = 'pax-t-divider',
  PAX_L_DIVIDER = 'pax-l-divider',
  PAX_PULL_OUT_MULTI_USE_HANGER = 'pax-pull-out-multi-use-hanger',
  PAX_CORNER_SOLUTION_ASM = 'pax-corner-solution',
  PAX_ADD_ON_CORNER_FRAME_ASM = 'pax-corner-addon-with-shelves',
  PAX_ADD_ON_CORNER_FRAME = 'pax-corner-addon-frame',
  PAX_CORNER_HINGE = 'pax-corner-hinge',
  PAX_INNER_LIGHT = 'light-strip',
  PAX_INNER_LIGHT_OVERSIDAN = 'light-strip-oversidan',
  PAX_OUTER_LIGHT = 'light-cabel',
  PAX_INTERIOR_SUGGESTIONS = 'pax-interior-suggestion',
  PAX_SKUBB_STORAGE_CASES = 'pax-skubb-storage-case',
  PAX_SKUBB_SHOE_BOXES = 'pax-skubb-shoe-box',
  PAX_SKUBB_HANGING_STORAGE = 'pax-skubb-hanging-storage',
  PAX_SKUBB_STORAGE_BOXES = 'pax-skubb-storage-box',
  PAX_SKUBB_STORAGE_BOXES_W_COMP = 'pax-skubb-storage-box-w-comp',
  PAX_SKUBB_STORAGE_CASE_WRAP = 'pax-skubb-storage-case-wrap',
  PAX_HEMMAFIXARE_STORAGE_CASE = 'hemmafixare-storage-case',
  PAX_HEMMAFIXARE_STORAGE_CASE_HIGH = 'hemmafixare-storage-case-high',
  PAX_HEMMAFIXARE_SHOE_BOX = 'hemmafixare-shoe-box',
  PAX_COVER_CAPS = 'pax-cover-caps',
  PAX_CORNER_MEASUREMENT_FRAME = 'pax-corner-measurement-frame',
  PAX_SHOE_MAT = 'pax-shoe-mat',
  PAX_CLEANING_INTERIOR = 'pax-cleaning-interior',

  // BESTÅ
  BESTA_FRAME_600_ASM = 'frame_w_legs-600-asm',
  BESTA_FRAME_1200_ASM = 'frame_w_legs-1200-asm',
  BESTA_FRAME = '23166',
  BESTA_TV_BENCH = '10333',
  BESTA_TV_BENCH_1200_ASM = 'tv_frame_w_legs-1200-asm',
  BESTA_TV_BENCH_1800_ASM = 'tv_frame_w_legs-1800-asm',
  BESTA_SHELF = '10972',
  BESTA_GLASS_SHELF = '10956',
  BESTA_LEG = 'leg',
  BESTA_DEFAULT_LEG = 'default-leg',
  BESTA_SUPPORTING_LEG = '17808',
  BESTA_DRAWER_FRAME = '31182',
  BESTA_DRAWER_FRONT = '11053',
  BESTA_DOOR_DRAWER_FRONT = '24179',
  BESTA_DOOR_192_ASM = 'door_w_hinges-1920-asm',
  BESTA_DOOR_64_ASM = 'door_w_hinges-640-asm',
  BESTA_DOOR_38_ASM = 'door_w_hinges-380-asm',
  BESTA_PUSH_OPEN = '31186',
  BESTA_SOFT_CLOSING = '31185',
  BESTA_DRAWER_26_ASM = 'drawer_w_rails_and_front-260-asm',
  BESTA_DRAWER_38_ASM = 'drawer_w_rails_and_front-380-asm',
  BESTA_HINGE_DOOR_SOFT_CLOSING = 'hinge_door_softclosing',
  BESTA_HINGE = 'besta-hinge',
  BESTA_TOP_PANEL = '10238',
  BESTA_TOP_PANEL_TV_BENCH = '29581',
  BESTA_FRAME_SUSPENSION_RAIL = '19643',
  BESTA_FRONT_LAYOUT = 'besta-front-layout',
  BESTA_BOTTLE_RACK = 'bottle_rack',
  BESTA_INNER_LIGHT = 'besta-inner-light',
  BESTA_SHELF_ORGANIZER = 'besta-shelf-organizer',
  BESTA_DRAWER_ORGANIZER = 'besta-drawer-organizer',
  BESTA_SHELF_AND_DRAWER_ORGANIZER = 'besta-shelf-and-drawer-organizer',

  // PLATSA
  PLATSA_FRAME = 'platsa-frame',
  PLATSA_OPEN_FRAME_SHELVES = 'platsa-open-frame-shelves',
  PLATSA_OPEN_FRAME_HANGING = 'platsa-open-frame-hanging',
  PLATSA_OPEN_FRAME_SHOES = 'platsa-open-frame-shoes',
  PLATSA_SHELF = 'platsa-shelf',
  PLATSA_SHOE_SHELF = 'platsa-shoe-shelf',
  PLATSA_WIRE_BASKET_ASM = 'platsa-wire-basket-asm',
  PLATSA_WIRE_BASKET_RAIL = 'platsa-rail-for-baskets',
  PLATSA_WIRE_BASKET = 'platsa-wire-basket',
  PLATSA_MULTIUSE_HANGER = 'platsa-multiuse-hanger',
  PLATSA_OUTSIDE_HOOK = 'platsa-outside-hook',
  PLATSA_LEG = 'platsa-leg',
  PLATSA_SUSPENSION_RAIL = 'platsa-suspension-rail',
  PLATSA_TOP = 'platsa-top',
  PLATSA_SLIDING_DOOR_ASM = 'platsa-sliding-door-asm',
  PLATSA_SLIDING_DOOR_FRONT = 'platsa-sliding-door-front',
  PLATSA_SLIDING_DOOR_RAIL = 'platsa-sliding-door-rail',
  PLATSA_SLIDING_DOOR_HANDLE = 'platsa-sliding-door-handle',
  PLATSA_HINGE = 'platsa-hinge',
  PLATSA_HINGE_DOOR_ASM = 'platsa-hinged-door-asm',
  PLATSA_SC_HINGE_DOOR_ASM = 'platsa-softclosing-hinged-door-asm',
  PLATSA_DOOR_FRONT = 'platsa-door-front',
  PLATSA_CLOTH_RAIL_40 = 'platsa-cloth-rail-40',
  PLATSA_CLOTH_RAIL_55 = 'platsa-cloth-rail-55',
  PLATSA_CLOTH_RAIL_55_W_HOOKS_ASM = 'platsa-cloth-rail-55-w-hooks-asm',
  PLATSA_CLOTH_RAIL_55_RAIL = 'platsa-cloth-rail-55-rail',
  PLATSA_INTERIOR_SUGGESTION = 'platsa-interior-suggestion',
  PLATSA_FRONT_LAYOUT = 'platsa-front-layout',
  PLATSA_DRAWER_ASM = 'platsa-drawer-asm',
  PLATSA_DRAWER_INSERT = 'platsa-drawer-insert',
  PLATSA_DRAWER_FRONT = 'platsa-drawer-front',
  PLATSA_HOOK = 'platsa-hook',
  PLATSA_RAIL_STRIP = 'platsa-cloth-rail-strip',
  PLATSA_RAIL_STRIP_W_HOOKS_ASM = 'platsa-rail-strip-w-hooks-asm',
  PLATSA_SHELF_AND_DRAWER_ORGANIZER = 'platsa-shelf-and-drawer-organizer',
  PLATSA_SHELF_ORGANIZER = 'platsa-shelf-organizer',
  PLATSA_CLOTH_RAIL_ORGANIZER = 'platsa-cloth-rail-organizer',
  PLATSA_INNER_LIGHT = 'light-strip',
  // EKET
  EKET_FRAME = 'eket-frame',
  EKET_MEDIA_FRAME = 'eket-media-frame',
  EKET_DOOR = 'eket-door',
  EKET_FRAME_WITH_GLASS_DOOR_ASM = 'eket-frame-w-glass-door-asm',
  EKET_LEG = 'eket-leg',
  EKET_SUSPENSION_RAIL = 'eket-suspension-rail',
  EKET_CONNECTION_FITTING = 'eket-connection-fittings',
  // SMÅSTAD
  SMASTAD_FRAME = 'smastad-frame',
  SMASTAD_FRAME_NA = 'smastad-frame-na',
  SMASTAD_FRAME_NA_ASM = 'smastad-frame-with-drawer-na-asm',
  SMASTAD_PULL_OUT_FRAME_108 = 'smastad-frame-w-pull-out-drawer-108',
  SMASTAD_PULL_OUT_FRAME_196 = 'smastad-frame-w-pull-out-drawer-196',
  SMASTAD_PULL_OUT_FRAME_DRAWER = 'smastad-frame-w-pull-out-drawer',
  SMASTAD_PULL_OUT_FRAME_108_ASM = 'smastad-frame-w-pull-out-drawer-asm',
  SMASTAD_PULL_OUT_FRAME_196_ASM = 'smastad-frame-w-pull-out-drawer-196-asm',
  SMASTAD_SHELF = 'smastad-shelf',
  SMASTAD_WIRE_BASKET_ASM = 'smastad-wire-basket-asm',
  SMASTAD_WIRE_BASKET_RAIL = 'smastad-rail-for-baskets',
  SMASTAD_WIRE_BASKET = 'smastad-wire-basket',
  SMASTAD_LEG = 'smastad-leg',
  SMASTAD_SUSPENSION_RAIL = 'smastad-suspension-rail',
  SMASTAD_TOP = 'smastad-top',
  SMASTAD_HINGE = 'smastad-hinge',
  SMASTAD_HINGE_DOOR_ASM = 'smastad-hinged-door-asm',
  SMASTAD_DOOR_FRONT = 'smastad-door-front',
  SMASTAD_CLOTH_RAIL_40 = 'smastad-cloth-rail-40',
  SMASTAD_CLOTH_RAIL_ASM = 'smastad-cloth-rail-asm',
  SMASTAD_CLOTH_RAIL_55_W_HOOKS_ASM = 'smastad-cloth-rail-55-w-hooks-asm',
  SMASTAD_CLOTH_RAIL_55_RAIL = 'smastad-cloth-rail-55-rail',
  SMASTAD_INTERIOR_SUGGESTION = 'smastad-interior-suggestion',
  SMASTAD_FRONT_LAYOUT = 'smastad-front-layout',
  SMASTAD_DRAWER_ASM = 'smastad-drawer-asm',
  SMASTAD_DRAWER_INSERT = 'smastad-drawer-insert',
  SMASTAD_DRAWER_FRONT = 'smastad-drawer-front',
  SMASTAD_HOOK = 'smastad-hook',
  SMASTAD_RAIL_STRIP = 'smastad-cloth-rail-strip',
  SMASTAD_RAIL_STRIP_W_HOOKS_ASM = 'smastad-rail-strip-w-hooks-asm',
  SMASTAD_BENCH = 'smastad-bench',
  SMASTAD_BENCH_DRAWER = 'smastad-bench-drawer',
  SMASTAD_BENCH_WITH_DRAWER_ASM = 'smastad-bench-with-drawer-asm',
  SMASTAD_DESK = 'smastad-desk',
  SMASTAD_CHANGING_TABLE = 'smastad-changing-table',
  SMASTAD_DESK_W_DRAWERS_ASM = 'smastad-desk-with-drawers-asm',
  SMASTAD_CHANGING_TABLE_W_DRAWERS_ASM = 'smastad-changing-table-with-drawers-asm',
  SMASTAD_BENCH_PAD = 'smastad-bench-pad',
  SMASTAD_BOX = 'smastad-box',
  SMASTAD_HANGING_STORAGE_CASE = 'smastad-hanging-storage-case',
  SMASTAD_FRAME_DRAWER_NA = 'smastad-frame-drawer',
  SMASTAD_MIRROR = 'smastad-mirror',
  SMASTAD_LABEL_HOLDER = 'smastad-label-holder',
  SMASTAD_HOOK_CLIP = 'smastad-hook-and-clip',
  SMASTAD_LOFT_BED = 'smastad-loft-bed',
  SMASTAD_LOFT_BED_ASM = 'smastad-loft-bed-asm',
  // IVAR
  IVAR_SECTION = 'section',
  IVAR_CORNER_SECTION = 'corner-section',
  IVAR_CORNER_SECTION_W_SHELVES = 'corner-section-w-inserts',
  IVAR_SIDE_UNIT = 'side-unit',
  IVAR_CORNER_POST = 'Posts',
  IVAR_CROSS_BRACE = 'cross-brace',
  IVAR_SHELF_PINE = 'shelf-pine',
  IVAR_SHELF_FELT = 'shelf-felt',
  IVAR_SHELF_METAL = 'shelf-metal',
  IVAR_CORNER_SHELF = 'shelf-pine-corner',
  IVAR_BOTTLE_RACK = 'bottle-rack',
  IVAR_CLOTHES_RAIL = 'clothing-rail',
  IVAR_DRAWER_PINE = 'drawer-pine',
  IVAR_DRAWER_METAL = 'drawer-metal',
  IVAR_TABLE = 'table',
  IVAR_STORAGE_BOX = 'storage-box',
  IVAR_UNDERFRAME = 'underframe',
  IVAR_DOOR = 'doors',
  IVAR_DOOR_ADJACENT = 'doors-adjacent',
  IVAR_DOOR_STANDING = 'doors-standing',
  IVAR_HOOKS = 'hooks',
  IVAR_END_PROTECTORS = 'end-protectors',
  IVAR_CABINET = 'cabinet',
  IVAR_CABINET_WITH_UNDERFRAME = 'cabinet-underframe',
  IVAR_INTERIOR_SUGGESTION = 'ivar-interior-suggestion',
  // BROR
  BROR_SECTION = 'section',
  BROR_SHELF_METAL_BLACK = 'shelf-metal-black',
  BROR_SHELF_WALL_METAL_BLACK = 'shelf-wall-metal-black',
  BROR_FOUR_PACK_SHELF = 'four-pack-shelf',
  BROR_WORK_BENCH = 'work-bench',
  BROR_CABINET_BLACK = 'cabinet-black',
  BROR_CABINET_GREEN = 'cabinet-green',
  BROR_CABINET_FLOOR = 'cabinet-floor',
  BROR_WALL_SECTION_UNIT = 'section-unit',
  BROR_WALL_SPR = 'wall-spr',
  BROR_PEGBOARD_SET = 'pegboard-set',
  BROR_TROLLEY = 'trolley',
  BROR_DRAWER_METAL_BLACK = 'drawer-metal-black',
  BROR_SHELF_BLACK_PINE = 'shelf-metal-black-pine',
  BROR_SHELF_GREEN_PINE = 'shelf-metal-green-pine',
  BROR_RAIL = 'rail-with-hooks',
  BROR_INTERIOR_SUGGESTION = 'bror-interior-suggestion',
  //ORGANIZERS
  SAMLA_ORGANIZER = 'samla-organizer',
  SOCKERBIT_ORGANIZER = 'sockerbit-organizer',
  KUGGIS_ORGANIZER = 'kuggis-organizer',
  KNAGGLIG_ORGANIZER = 'knagglig-organizer',
  KLAMTARE_ORGANIZER = 'klamtare-organizer',
  RISATORP_ORGANIZER = 'risatorp-organizer',
  // BILLY
  BILLY_FRAME = 'billy-frame',
  BILLY_FRAME_WITH_SHELVES_ASM = 'billy-frame-asm',
  BILLY_DOOR_ASM = 'billy-door-asm',
  BILLY_DOOR = 'billy-door-front',
  BILLY_SHELF = 'billy-shelf',
  BILLY_DRAWER = 'billy-drawer',
  BILLY_FRAME_EXTENSION = 'billy-frame-ext',
  BILLY_CORNER_FITTING = 'billy-corner-fitting',
  BILLY_DESK = 'billy-desk',
  // KALLAX
  KALLAX_FRAME = 'frame',
  KALLAX_DRAWERS = 'drawers',
  KALLAX_CABINETS = 'cabinets',
  KALLAX_DIVIDERS = 'dividers',
  KALLAX_BOXES = 'boxes',
  KALLAX_BASKETS = 'baskets',
  KALLAX_UNDERFRAME = 'underframe',
  KALLAX_ASSEMBLY = 'kallax-assembly',
  KALLAX_DESK = 'desk',
  KALLAX_TV_BENCH = 'tv-bench',
  // BOAXEL
  BOAXEL_UPRIGHT = 'upright',
  BOAXEL_SHELF = 'shelf',
  BOAXEL_ADJ_SHELF = 'adjustable-shelf',
  BOAXEL_ADJ_SHELF_EXT = 'adjustable-shelf-ext',
  BOAXEL_ADJ_CLOTHES_RAIL_EXT = 'adjustable-clothes-rail-ext',
  BOAXEL_SHOE_SHELF = 'shoe-shelf',
  BOAXEL_WIRE_SHELF = 'wire-shelf',
  BOAXEL_METAL_SHELF = 'metal-shelf',
  BOAXEL_DRYING_RACK = 'drying-rack',
  BOAXEL_UPRIGHT_SECTION = 'section',
  BOAXEL_MESH_BASKET = 'mesh-basket',
  BOAXEL_CLOTHES_RAIL = 'clothes-rail',
  BOAXEL_ADJ_CLOTHES_RAIL = 'adjustable-clothes-rail',
  BOAXEL_BRACKET = 'bracket',
  BOAXEL_INSERT = 'insert',
  BOAXEL_MOUNTING_RAIL = 'mounting-rail',
  BOAXEL_MOUNTING_RAIL_EXT = 'mounting-rail-ext',
  BOAXEL_NON_MOUNTING_RAIL = 'non-mounting-rail',
  // LADMAKARE
  LADMAKARE_SECTION_WITH_SHELVES = 'ladmakare-section-with-shelves',
  LADMAKARE_SECTION = 'ladmakare-section',
  LADMAKARE_COMPARTMENT = 'ladmakare-compartment',
  LADMAKARE_SHELF = 'ladmakare-shelf',
  LADMAKARE_CABINET = 'ladmakare-cabinet',
  LADMAKARE_PANEL = 'ladmakare-panel',
  // JONAXEL
  JONAXEL_FRAME = 'jonaxel-frame',
  JONAXEL_FRAME_SHELF = 'jonaxel-frame-shelf',
  JONAXEL_WIRE_BASKET = 'jonaxel-wire-basket',
  JONAXEL_MESH_BASKET = 'jonaxel-mesh-basket',
  JONAXEL_TOP_SHELF = 'jonaxel-top-shelf',
  JONAXEL_INTERIOR_SUGGESTION = 'jonaxel-interior-suggestion',
  JONAXEL_CLOTH_RAIL_ADJUSTABLE = 'jonaxel-clothing-rail-adjustable',
  // ELVARLI
  ELVARLI_SHELF_WHITE = 'shelf-white',
  ELVARLI_SHELF_BAMBOO = 'shelf-bamboo',
  ELVARLI_SHOE_SHELF = 'shoe-shelf',
  ELVARLI_DRAWER = 'drawer',
  ELVARLI_SECTION = 'section',
  // KNOXHULT
  KNOXHULT_CABINET = 'KNOXHULT-cabinet',
  KNOXHULT_WALL_CABINET = 'KNOXHULT-wall-cabinet',
  KNOXHULT_CORNER_CABINET = 'KNOXHULT-corner-cabinet',
  KNOXHULT_HIGH_CABINET = 'KNOXHULT-high-cabinet',
  KNOXHULT_DOOR_FRONT = 'KNOXHULT-door-front',
  KNOXHULT_DRAWER_FRONT = 'KNOXHULT-drawer-front',
  KNOXHULT_WORKTOP = 'KNOXHULT-worktop',
  KNOXHULT_CABINET_ASM = 'KNOXHULT-cabinet-asm',
  KNOXHULT_CORNER_CABINET_ASM = 'KNOXHULT-corner-cabinet-asm',
  KNOXHULT_SINK_CABINET_ASM = 'KNOXHULT-sink-cabinet-asm',
  KNOXHULT_SINK_AND_OVEN_CABINET_ASM = 'KNOXHULT-sink-and-oven-cabinet-asm',
  KNOXHULT_NICHE_CABINET_ASM = 'KNOXHULT-niche-cabinet-asm',
  KNOXHULT_WALL_CABINET_ASM = 'KNOXHULT-wall-cabinet-asm',
  KNOXHULT_WALL_GLASS_CABINET_ASM = 'KNOXHULT-wall-glass-cabinet-asm',
  KNOXHULT_HIGH_CABINET_ASM = 'KNOXHULT-high-cabinet-asm',
  ENHET_WALL_FRAME = 'wall fr w shelves',
  //Appliances
  APPLIANCE_INTEGRATED_HOB = 'hob',
  APPLIANCE_INTEGRATED_OVEN = 'integrated-oven',
  APPLIANCE_FRIDGE_FREEZER = 'fridge-freezer',
  APPLIANCE_FREE_HANGING_EXTRACTOR_HOOD = 'free-hanging-extractor-hood',
  APPLIANCE_CABINET_EXTRACTOR_HOOD = 'cabinet-extractor-hood',
  // Kitchen Sinks
  KITCHEN_SINK_ASM = 'sink',
  // Kitchen Taps
  KITCHEN_TAP = 'tap',
  // AURADAL
  AURDAL_FRAME = 'section',
  AURDAL_SHELF = 'shelf',
  AURDAL_SIDEPANEL = 'side-unit',
  AURDAL_DRAWER = 'drawer',
  AURDAL_BASKET = 'wire-basket',
  AURDAL_SHOERACK = 'shoe-shelf',
  AURDAL_EXT_CLOTH_RAIL = 'extendable-clothes-rail',
  AURDAL_CLOTH_RAIL = 'clothes-rail',
  AURDAL_SUSPENSION_RAIL = 'suspension-rail'
}

export enum PartKey {
  CORNER_SOLUTION_NORMAL_FRAME = 'normal-frame',
  CORNER_SOLUTION_CORNER_FRAME = 'corner-frame',
  CORNER_SOLUTION_MEASUREMENT_FRAME = 'pax-corner-measurement-frame',
  LEG_1 = 'leg-1',
  LEG_2 = 'leg-2',
  LEG_3 = 'leg-3',
  LEG_4 = 'leg-4',
  LEG_5 = 'leg-5',
  LEG_6 = 'leg-6',
  LEG_7 = 'leg-7',
  LEG_8 = 'leg-8',
  SHARED_LEG_1 = 'leg-shared-1',
  SHARED_LEG_2 = 'leg-shared-2',
  TRAY = 'tray',
  FRAME = 'frame',
  SLIDING_DOOR_PANEL_FRAME_RAIL = 'rail-frame',
  SLIDING_DOOR_PANEL_FRAME_LEFT = 'frame-1',
  SLIDING_DOOR_PANEL_FRAME_RIGHT = 'frame-2',
  SLIDING_DOOR_REGULAR_FRAME = 'door-frame',
  SLIDING_DOOR_SOFT_CLOSING_DEVICE_LEFT = 'soft-closing-1',
  SLIDING_DOOR_SOFT_CLOSING_DEVICE_RIGHT = 'soft-closing-2',
  HINGE_1 = 'hinge-1',
  HINGE_2 = 'hinge-2',
  FRONT = 'front',
  DRAWER_FRAME = 'body',
  DRAWER_RUNNER_LEFT = 'runner-left',
  DRAWER_RUNNER_RIGHT = 'runner-right',
  SUSPENSION_RAIL_1 = 'suspension_rail-1',
  SUSPENSION_RAIL_2 = 'suspension_rail-2',
  DRAWER_ASSEMBLY_BODY = 'body',
  DRAWER_ASSEMBLY_FRONT = 'front',
  PANEL_1 = 'panel-1',
  PANEL_2 = 'panel-2',
  PANEL_3 = 'panel-3',
  PANEL_4 = 'panel-4',
  PANEL_5 = 'panel-5',
  PANEL_6 = 'panel-6',
  PANEL_7 = 'panel-7',
  PANEL_8 = 'panel-8',
  MAT = 'mat',
  IVAR_CABINET = 'cabinet',
  SIDE_UNIT_LEFT = 'side-unit-left',
  SIDE_UNIT_RIGHT = 'side-unit-right',
  PANEL = 'panel-asm',
  KNOXHULT_WORKTOP_LEFT = 'worktop-left',
  KNOXHULT_WORKTOP_RIGHT = 'worktop-right',
  KNOXHULT_WORKTOP_CENTER = 'worktop-center'
}

export type HandleType = ProductType.HANDLE | ProductType.KNOB | ProductType.EDGE_HANDLE;

export enum Connectors {
  // Pax connectors between doors and frames
  DOOR_CONNECTION_LEFT = 'attach-frame-left',
  DOOR_CONNECTION_RIGHT = 'attach-frame-right',
  DOOR_LEFT = 'left-door-placement',
  DOOR_RIGHT = 'right-door-placement',
  DOOR_CONNECTION = 'door-connection',
  // Bestå connectors between doors and frames
  // Right connector for besta
  ATTACH_FRAME = 'attach-frame',
  // Left connector for besta
  ATTACH_FRAME_2 = 'attach-frame-2',
  // These comes numbered from right-series-1-4 for larger bestå frames 1-4 is the hole number
  RIGHT_SERIES = 'right-series',
  LEFT_SERIES = 'left-series',
  // PLATSA frame connectors
  PLATSA_FRAME_RIGHT_SERIES = 'right-door-series',
  PLATSA_FRAME_LEFT_SERIES = 'left-door-series',
  // PLATSA front connectors
  PLATSA_FRONT_CONNECTOR_TOP = 'attach-frame-top',
  PLATSA_FRONT_CONNECTOR_BOTTOM = 'attach-frame-bottom'
}

export enum ProductDesign {
  WHITE = 'white',
  BLACK = 'black',
  BLACK_BROWN = 'black brown',
  DARK_GREY = 'dark grey',
  DARK_GREY_METAL = 'dark grey metal',
  OAK = 'oak effect',
  BLACK_OAK = 'black oak effect',
  DARK_BROWN_OAK = 'dark brown oak effect',
  GLASS = 'glass',
  BIRCH = 'birch effect',
  BROWN_STAINED_ASH = 'brown stained ash effect',
  GREY_STAINED_ASH = 'grey stained ash effect',
  WHITE_STAINED_OAK = 'white stained oak effect',
  GREY_STAINED_WALNUT = 'grey stained walnut effect',
  BEIGE = 'beige',
  LIGHT_GREEN = 'light green',
  PALE_PINK = 'pale pink',
  LIGHT_GREY = 'light grey',
  BROWN_WALNUT_EFFECT = 'brown walnut effect',
  RED_BROWN = 'red-brown',
  METAL = 'metal',
  WOOD = 'wood',
  GREY_GREEN = 'grey-green',
  LIGHT_GREY_BLUE = 'light grey-blue',
  PALE_YELLOW = 'pale yellow',
  DARK_GREEN = 'dark green',
  HIGH_GLOSS_WHITE = 'high gloss white',
  GREY_WOOD_EFFECT = 'grey wood effect',
  WALNUT_EFFECT_LIGHT_GREY = 'walnut effect light grey',
  ANTHRACITE = 'anthracite',
  METAL_WHITE = 'metal white',
  METAL_ANTHRACITE = 'metal anthracite',
  GREY = 'grey',
  WHITE_FRAME = 'white frame'
}

export enum ProductIdentifiers {
  MIRRORED_PRODUCT = 'mirrored'
}

export type Range =
  | 'pax'
  | 'besta'
  | 'platsa'
  | 'eket'
  | 'smastad'
  | 'ivar'
  | 'billy'
  | 'bror'
  | 'kallax'
  | 'boaxel'
  | 'ladmakare'
  | 'jonaxel'
  | 'elvarli'
  | 'knoxhult'
  | 'aurdal';

export enum LegacyRangeIds {
  PAX = 'PAX',
  BESTA = 'BESTA',
  PLATSA = 'PLATSA',
  EKET = 'EKET',
  SMASTAD = 'SMASTAD',
  IVAR = 'IVAR',
  BROR = 'BROR',
  KALLAX = 'KALLAX',
  BOAXEL = 'BOAXEL',
  LADMAKARE = 'LADMAKARE',
  JONAXEL = 'JONAXEL',
  ELVARLI = 'ELVARLI'
}

export enum VPCImageType {
  MAIN = 'main-design-image',
  OPEN_DESIGN = 'open-design-image'
}

export interface VpcConfigurationImage {
  typeCode: VPCImageType;
  image: string;
}

export type OpeningFunction = ProductType.BESTA_PUSH_OPEN | ProductType.BESTA_SOFT_CLOSING;

export enum AssemblyInstructionMeasurementPosition {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom'
}

export type AssemblyInstructionMeasurementPoint = {
  coord: IVector2;
  positionAlongWall: number;
};

export type AssemblyInstructionEntityPoint = {
  coord: IVector2;
  instructionNbr: number;
  entity: Entity;
};

export type AssemblyInstructionMeasurementData = {
  [key in AssemblyInstructionMeasurementPosition]?: AssemblyInstructionMeasurementPoint[];
};
